import axios from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../../constants';
import { Customer } from '../../state/v1/Customer';
import { Employee } from '../../state/v1/Employee';
import { Invoice } from '../../state/v1/Invoice';
import { Location } from '../../state/v1/Location';
import {
  RefundIntentPayload,
  RefundIntentResponse,
  UpsertRefundIntentParams,
} from '../../state/v1/RefundIntent';
import { formatRefId } from '../../utils/referenceHelpers';
import { CustomerData } from './CreditAccountService';
import { getOrCreateCreditAccountId, putWithCreditAccountRetry } from './putWithCreditAccountRetry';

/**
 * Builds an invoicing Location from R-Series shop data.
 */
export function buildLocationFromShopData(shopData: {
  shopID: string;
  name?: string;
  Contact?: {
    Emails?: { ContactEmail?: Array<{ address: string }> };
    Addresses?: {
      ContactAddress?: Array<{
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
      }>;
    };
  };
}): Location {
  const contactAddress = shopData.Contact?.Addresses?.ContactAddress?.[0];
  return {
    locationRef: `${shopData.shopID}`,
    name: shopData.name || '',
    email: shopData.Contact?.Emails?.ContactEmail?.[0]?.address || '',
    address: {
      addressType: 'contact',
      street1: contactAddress?.address1 || '',
      street2: contactAddress?.address2 || '',
      city: contactAddress?.city || '',
      state: contactAddress?.state || '',
      stateCode: '',
      country: contactAddress?.country || '',
      countryCode: '',
      postalCode: contactAddress?.zip || '',
    },
  };
}

/**
 * Builds an invoicing Customer from R-Series customer data.
 */
export function buildCustomerFromData(customerData?: CustomerData): Customer {
  if (!customerData) {
    return {
      customerRef: '',
      companyName: '',
      title: '',
      firstName: '',
      lastName: '',
      emails: [],
      addresses: [],
      phoneNumbers: [],
    };
  }

  const emails: Customer['emails'] = [];
  const contactEmails = customerData.Contact?.Emails;
  if (contactEmails && contactEmails.ContactEmail) {
    const emailList = Array.isArray(contactEmails.ContactEmail)
      ? contactEmails.ContactEmail
      : [contactEmails.ContactEmail];
    emailList.forEach((emailObj) => {
      if (emailObj?.address) {
        emails.push({
          email: emailObj.address,
          isPrimary: emailObj.useType === 'Primary',
        });
      }
    });
  }

  const addresses: Customer['addresses'] = [];
  const contactAddresses = customerData.Contact?.Addresses;
  if (contactAddresses && contactAddresses.ContactAddress) {
    const addressList = Array.isArray(contactAddresses.ContactAddress)
      ? contactAddresses.ContactAddress
      : [contactAddresses.ContactAddress];
    addressList.forEach((addr) => {
      if (addr) {
        addresses.push({
          addressType: 'contact',
          street1: addr.address1 || '',
          street2: addr.address2 || '',
          city: addr.city || '',
          state: addr.state || '',
          stateCode: addr.stateCode || '',
          country: addr.countryCode || '',
          countryCode: addr.countryCode || '',
          postalCode: addr.zip || '',
        });
      }
    });
  }

  const phoneNumbers: Customer['phoneNumbers'] = [];
  const contactPhones = customerData.Contact?.Phones;
  if (contactPhones && contactPhones.ContactPhone) {
    const phoneList = Array.isArray(contactPhones.ContactPhone)
      ? contactPhones.ContactPhone
      : [contactPhones.ContactPhone];
    phoneList.forEach((phoneObj) => {
      if (phoneObj?.number) {
        phoneNumbers.push({
          type: 'other',
          phoneNumber: phoneObj.number,
        });
      }
    });
  }

  return {
    customerRef: formatRefId(`${customerData.customerID}`),
    companyName: customerData.company || '',
    title: customerData.title || '',
    firstName: customerData.firstName || '',
    lastName: customerData.lastName || '',
    emails,
    addresses,
    phoneNumbers,
  };
}

/**
 * Builds an invoicing Employee from R-Series employee data.
 */
export function buildEmployeeFromData(employeeData?: {
  employeeID: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}): Employee {
  if (!employeeData) {
    return { employeeRef: '', firstName: '', lastName: '', email: '' };
  }

  return {
    employeeRef: formatRefId(employeeData.employeeID),
    firstName: employeeData.firstName || '',
    lastName: employeeData.lastName || '',
    email: employeeData.email || '',
  };
}

/**
 * Builds an Invoice object from invoice data and transaction details.
 *
 * @param saleId The sale/transaction ID
 * @param registerRef The register reference (will be formatted with ls-retail_ prefix)
 * @param currency The currency code (e.g., 'USD', 'CAD')
 * @param customer The customer data for the invoice
 * @param employee The employee data for the invoice
 * @param location The location data for the invoice
 * @param dueDateOffset Number of days from now for the due date (default: 0)
 * @returns An Invoice object ready to be sent to the API
 */
export function buildRefundIntentInvoiceCreate(
  saleId: string,
  registerRef: string,
  currency: string,
  customer: Customer,
  employee: Employee,
  location: Location,
  dueDateOffset: number = 0
): Invoice {
  // Calculate dates
  const now = new Date();
  const issueDateStr = now.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  const dueDate = new Date(now.getTime() + dueDateOffset * 24 * 60 * 60 * 1000);
  const dueDateStr = dueDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD

  return {
    transactionRef: formatRefId(saleId),
    currency,
    total: '0.00',
    subtotal: '0.00',
    taxableTotal: '0.00',
    taxTotal: '0.00',
    discountTotal: '0.00',
    feesTotal: '0.00',
    lineItems: [],
    customer,
    location,
    registerRef: formatRefId(registerRef),
    employee,
    issueDate: issueDateStr,
    deposits: [],
    invoiceIntentId: '',
    note: '',
    payments: [],
    saleTaxCalculations: [],
    terms: {
      dueDate: dueDateStr,
      sendByEmail: false,
      customerEmail: '',
      notes: '',
    },
  };
}

export default class RefundIntentService {
  /**
   * Creates or updates a refund intent for a transaction.
   * If the customer doesn't have a credit account on R-Series, it will create one with $0 limit.
   * If the credit account doesn't exist on Invoicing (404), it will create it and retry.
   *
   * @param params The parameters for upserting a refund intent
   * @returns The refund intent response with the credit account ID used
   */
  static async upsertRefundIntent(
    params: UpsertRefundIntentParams & { customerData?: CustomerData }
  ): Promise<RefundIntentResponse & { creditAccountId: string }> {
    const { customerId, saleId, amount, currency, accountId, registerRef, invoice } = params;

    if (!saleId) {
      throw new Error(`Missing required parameter: saleId=${saleId}`);
    }

    const creditAccountIdStr = await getOrCreateCreditAccountId(params.creditAccountId, customerId);
    const creditAccountRef = formatRefId(creditAccountIdStr);
    const transactionRef = formatRefId(`${saleId}`);

    const invoiceCreate = buildRefundIntentInvoiceCreate(
      saleId,
      registerRef,
      currency,
      invoice.customer,
      invoice.employee,
      invoice.location
    );

    // API expects totalAmount as a string-encoded decimal (positive value)
    const payload: RefundIntentPayload = {
      totalAmount: amount.toString(),
      currency,
      invoiceCreate: {
        ...invoiceCreate,
        creditAccountRef,
        accountRef: formatRefId(accountId),
      },
      depositUsageIntent: {
        totalAmount: amount.toString(),
        currency,
        autoAllocate: true,
      },
    };

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/refund-intent`;

    return putWithCreditAccountRetry<RefundIntentPayload, RefundIntentResponse>({
      url,
      payload,
      creditAccountIdStr,
      creditAccountRef,
      transactionRef,
      currency,
      customer: params.customerData,
      operationName: 'refund intent',
      datadogContext: { amount },
    });
  }

  /**
   * Deletes a refund intent for a transaction.
   *
   * @param creditAccountId The credit account ID from R-Series
   * @param saleId The sale/transaction ID
   */
  static async deleteRefundIntent(creditAccountId: string | number, saleId: string): Promise<void> {
    if (!saleId) {
      throw new Error(`Missing required parameter: saleId=${saleId}`);
    }

    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0') {
      throw new Error(`Missing required parameter: creditAccountId=${creditAccountId}`);
    }

    const creditAccountRef = formatRefId(`${creditAccountId}`);
    const transactionRef = formatRefId(`${saleId}`);

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/refund-intent`;

    try {
      await axios.delete(url);
    } catch (error) {
      datadogRum.addError(error, {
        creditAccountRef,
        transactionRef,
        message: `Failed to delete refund intent for transactionRef: ${transactionRef}`,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
